import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Minimum 2 znaki").max(100),
  email: z.string().trim().email("Podaj prawidłowy email").max(255),
  phone: z.string().min(9, "Podaj numer telefonu"),
  message: z.string().trim().min(10, "Minimum 10 znaków").max(1000),
  consent: z.literal(true, { errorMap: () => ({ message: "Wymagana zgoda" }) }),
});

type ContactFormData = z.infer<typeof contactSchema>;

const CTASection = () => {
  const reduceMotion = useReducedMotion();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = useIsMobile();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", phone: "", message: "", consent: false as any },
  });

  const [honeypot, setHoneypot] = useState("");

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name: data.name, email: data.email, phone: data.phone, message: data.message, website: honeypot },
      });
      if (error) {
        toast.error("Wystąpił błąd przy wysyłaniu wiadomości. Spróbuj ponownie.");
        return;
      }
      toast.success("Wiadomość wysłana! Odezwiemy się wkrótce.");
      form.reset();
    } catch {
      toast.error("Wystąpił błąd. Spróbuj ponownie później.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const anim = (delay = 0) =>
    isMobile
      ? { initial: false as const, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0 } }
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, delay },
        };

  return (
    <section id="kontakt" className="relative py-24 md:py-36 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          className="flex flex-col gap-6 mb-8"
          {...anim()}
        >
          <div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-foreground" style={{ textShadow: '0 0 40px hsl(38 40% 60% / 0.3), 0 0 80px hsl(38 40% 60% / 0.15)' }}>
              Porozmawiajmy.
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-lg">
              Powiedz nam, co planujesz. Odezwiemy się z propozycją.
            </p>
          </div>
        </motion.div>

        <div className="border-t border-border/40 mb-12" />

        {/* Two columns */}
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left — form */}
          <motion.div {...anim(0.1)}>
            <p className="text-sm text-muted-foreground mb-8 tracking-wide uppercase">
              Wolisz napisać?
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Honeypot — hidden from users, catches bots */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="cta-website">Website</label>
                  <input id="cta-website" type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                </div>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Imię i nazwisko"
                          className="bg-transparent border-0 border-b border-border/50 rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary/60"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Email"
                          className="bg-transparent border-0 border-b border-border/50 rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary/60"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Telefon"
                          className="bg-transparent border-0 border-b border-border/50 rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary/60"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Twoja wiadomość"
                          rows={4}
                          className="bg-transparent border-0 border-b border-border/50 rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary/60 resize-none min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="consent"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start gap-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-0.5"
                            aria-label="Zgoda na przetwarzanie danych osobowych"
                          />
                        </FormControl>
                        <label className="text-xs text-muted-foreground leading-relaxed cursor-pointer" onClick={() => field.onChange(!field.value)}>
                          Wyrażam zgodę na przetwarzanie moich danych osobowych w celu odpowiedzi na zapytanie.{" "}
                          <Link to="/polityka-prywatnosci" className="text-primary hover:underline">Polityka prywatności</Link>
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors text-sm font-medium pt-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wysyłanie...
                    </>
                  ) : (
                    <>
                      Wyślij wiadomość
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </Form>
          </motion.div>

          {/* Right — phone */}
          <motion.div className="flex flex-col" {...anim(0.2)}>
            <p className="text-sm text-muted-foreground mb-8 tracking-wide uppercase">
              Wolisz zadzwonić?
            </p>
            <a
              href="tel:+48601131339"
              className="text-3xl lg:text-4xl font-medium text-foreground hover:text-primary transition-colors"
            >
              +48 601 13 13 39
            </a>
            <a href="mailto:r.wilk@studioskandal.pl" className="mt-4 text-base text-muted-foreground hover:text-foreground transition-colors">
              r.wilk@studioskandal.pl
            </a>
            <a href="https://share.google/ljqWJfHfCd0GNOlez" target="_blank" rel="noopener noreferrer" className="mt-2 text-base text-muted-foreground hover:text-foreground transition-colors">
              Sienkiewicza 4, Warszawa
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
